/* eslint-disable ember/no-runloop */
import type { Timer } from "@ember/runloop";
import { cancel } from "@ember/runloop";
import { service } from "@ember/service";
import Base from "ember-simple-auth/authenticators/base";
import type SemeiaSession from "parog-web/services/session";
import ControlTower from "wise-control-tower";

interface UserAuthData {
  as: string;
  id: string;
  uuid: string;

  access_token: string;
  refresh_token?: string;
  two_factor_auth_token?: string;
}

export interface MedicAuthData extends UserAuthData {
  role: string;
  is_super_user?: boolean;
  first_health_centre_uuid?: string;

  browser_auth_token?: string;
  id_token?: string;
}

export interface PatientAuthData extends UserAuthData {
  health_centre_id: number;
  health_centre_uuid: string;
}

type AuthData = MedicAuthData | PatientAuthData;

export default abstract class BaseAuthenticator<
  T extends AuthData
> extends Base {
  @service requestManager;
  @service declare session: SemeiaSession;

  declare _refreshTokenTimeout?: Timer;
  abstract loginEndpoint: string;
  abstract userClassName: "Medic" | "Patient";

  abstract override authenticate(args: unknown): Promise<T | undefined>;

  override async invalidate(): Promise<void> {
    if (this.session.user?.refreshTokensEnabled) {
      await this.session.revokeToken();
    }
    cancel(this._refreshTokenTimeout);
    delete this._refreshTokenTimeout;
    await ControlTower.resetUser();
  }

  override async restore(data: T): Promise<T> {
    if (data.refresh_token) {
      // Do not authenticate if refresh token is expired
      if (!this.session.isTokenValid(data.refresh_token)) {
        await ControlTower.resetUser();
        throw "Base Password Authenticator - Invalid refresh token";
      }

      // If access token is expired, refresh it and the RT
      if (!this.session.isTokenValid(data.access_token)) {
        await this.session.refreshTokens();
      }

      // Otherwise, do nothing and schedule next tokens refresh
      this.session.scheduleNextRefresh(data.access_token);
    }

    await ControlTower.setUser({ ...data, class: this.userClassName });

    await this.session.fetchAuthenticatedUser(data);

    return data;
  }

  protected async onAuthenticationFinished(
    data: T | undefined
  ): Promise<T | undefined> {
    if (data) {
      await ControlTower.setUser({ ...data, class: this.userClassName });
    } else {
      await ControlTower.resetUser();
    }

    this.session.isAuthenticatedAtThisSession = true;
    if (data !== undefined) {
      await this.session.fetchAuthenticatedUser(data);
    }
    if (this.session.user?.refreshTokensEnabled) {
      this.session.scheduleNextRefresh(data?.access_token);
    }
    return data;
  }

  protected async sendLoginRequest(authData: any): Promise<T> {
    const body = JSON.stringify({
      auth: authData
    });

    try {
      const { content } = await this.requestManager.request({
        url: this.loginEndpoint,
        method: "POST",
        body: body
      });

      return content;
    } catch ({ response, content }) {
      if (!response.ok) throw response;
      return content;
    }
  }
}
